import User from '~/model/user';
import { Store } from 'react-notifications-component';
import config from '~/config';
import UserRole from '~/model/userRole';
import DOMPurify from 'dompurify';
import regx from '~/constants/regx';
import { WorkingHoursEntry, WEEKDAYS } from '~/constants/weekday';

export const sleep = (time: number) => new Promise((resolve) => setTimeout(resolve, time));

export const getSharedPaginationOptions = () => ({
  custom: true,
  hidePageListOnlyOnePage: true,
  paginationSize: 10,
  sizePerPageList: [
    { text: '1', value: 1 },
    { text: '50', value: 50 },
    { text: '100', value: 100 },
    { text: '200', value: 200 },
  ],
});

export const getSharedTableOptions = () => ({
  keyField: "id",
  bordered: false,
  striped: false,
  defaultSortDirection: "asc",
  selectRow: {
    mode: 'radio',
    hideSelectColumn: true,
  },
  classes: "table align-middle table-nowrap",
  headerWrapperClasses: "thead-light",
  responsive: true,
  remote: true,
});

export const valueIsDefined = (value: any) => value !== undefined && value !== null;

export const valueIsEmpty = (value: any) => {
  if (typeof value === 'string') {
    value = value.trim();
  }
  // allow 0 and FALSE
  return value !== 0 && value !== false && !value;
}

export const mapNonEmpty = (obj: any) => Object.entries(obj).reduce((a: any, [k, v]) => (!valueIsEmpty(v) ? (a[k] = v, a) : a), {});

export const hasNonEmpty = (obj: any) => Object.keys(mapNonEmpty(obj)).length > 0;

// axios serializes only plain objects
// so any nested objects will end up as json
// but we need the filters as a query string array
// so here we do the transformation
export const flattenFilters = (params: any) => {
  // clone the object to avoid changing the original
  const newParams = { ...params };
  if (newParams.filters) {
    // loop through all filters
    for (const [key, value] of Object.entries(newParams.filters)) {
      // if the filter value is not empty
      if (!valueIsEmpty(value)) {
        // add the filter under the root object
        // change the key so it will be decoded properly on the backend
        newParams[`filters[${key}]`] = value;
      }
    }
    // remove the filters key since it is no longer needed
    delete newParams.filters;
  }
  return newParams;
}

// the Select control expects a list of {value, label} objects as options
// most of the time api response is different than what the Select control expects
// so here we do the transformation
export const toSelectOptions = (list: any[], valueProp = 'id', labelProp = 'name') => {
  return list?.map(item => ({
    label: item[labelProp],
    value: item[valueProp],
  }));
}

export const getYesNoOptions = (includeAll = false) => {
  const options: { label: string, value: string | number }[] = [{
    label: "Yes",
    value: 1,
  }, {
    label: "No",
    value: 0
  }];
  if (includeAll) {
    options.unshift({
      label: "All",
      value: '',
    });
  }
  return options;
}

export const isObject = (obj: any) => typeof obj === 'object' && obj !== null;

export const showNotification = (type: string, message: string, options: any) => {
  const defaultOptions = {
    title: '',
    insert: 'top',
    container: 'top-center',
    animationIn: ['animate__animated', 'animate__fadeIn'],
    animationOut: ['animate__animated', 'animate__fadeOut'],
    dismiss: {
      duration: 6000,
      onScreen: true,
      pauseOnHover: true,
      showIcon: true,
    },
    width: 400,
  }
  return Store.addNotification({
    ...defaultOptions,
    ...options,
    type: type,
    message: message,
  });
}

export const showBriefNotification = (type: string, message: string, options: any) => {
  const defaultOptions = {
    title: '',
    insert: 'top',
    container: 'top-center',
    animationIn: ['animate__animated', 'animate__fadeIn'],
    animationOut: ['animate__animated', 'animate__fadeOut'],
    dismiss: {
      duration: 2000,
      onScreen: true,
      pauseOnHover: true,
      showIcon: true,
    },
    width: 400,
  }
  return Store.addNotification({
    ...defaultOptions,
    ...options,
    type: type,
    message: message,
  });
}

export const showSuccess = (message: string, options?: any) => showNotification('success', message, options);

export const showBriefSuccess = (message: string, options?: any) => showBriefNotification('success', message, options);

export const showMessage = (message: string, options?: any) => showNotification('info', message, options);

export const showWarning = (message: string, options?: any) => showNotification('warning', message, options);

export const showError = (message: string, options?: any) => showNotification('danger', message, options);

export const showBriefError = (message: string, options?: any) => showBriefNotification('danger', message, options);

export const removeNotification = (id: string) => Store.removeNotification(id);

export const getChangeEventData = (e: any, meta: any) => {
  let name, value;
  if (e.target) {
    name = e.target.name;
    value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
  } else {
    // meta is only available for Select
    name = meta.name;
    // the Select component only accepts an object as value
    // so here we return the entire selected option
    value = e;
  }
  return [name, value];
}

export const trim = (str: string, char: string) => {
  char = char.replace(/[-/^$*+?.()|[]{}]/g, '$&');
  return str.replace(new RegExp(
    "^[" + char + "]+|[" + char + "]+$", "g"
  ), "");
}

export const ltrim = (s: string, c: string) => {
  c = c.replace(/[-/^$*+?.()|[]{}]/g, '$&');
  return s.replace(new RegExp(
    "^[" + c + "]+", "g"
  ), "");
}

export const rtrim = (s: string, c: string) => {
  c = c.replace(/[-/^$*+?.()|[]{}]/g, '$&');
  return s.replace(new RegExp(
    "[" + c + "]+$", "g"
  ), "");
}

export const buildUrl = (base: string, fragment: string) => rtrim(base, '/') + (fragment ? '/' + ltrim(fragment, '/') : '');

export const getBeUrl = (fragment?: string) => buildUrl(config.API_BE_URL as string, fragment as string);

export const getAdminAppUrl = (fragment?: string) => buildUrl(config.ADMIN_URL as string, fragment as string);

export const getDealerAppUrl = (fragment?: string) => buildUrl(config.DEALER_URL as string, fragment as string);

export const getNotaryAppUrl = (fragment?: string) => buildUrl(config.NOTARY_URL as string, fragment as string);

export const nullsToEmptyStrings = (obj: any) => {
  if (!obj) {
    return;
  }
  if (Array.isArray(obj)) {
    return obj.map(v => v === null ? '' : v);
  } else {
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v === null ? '' : v]));
  }
}

export const toUserModel = (user: any) => Object.assign(new User(), user);

const US_TIMEZONES = [
  'America/New_York',
  'America/Detroit',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Indiana/Indianapolis',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Vevay',
  'America/Chicago',
  'America/Indiana/Tell_City',
  'America/Indiana/Knox',
  'America/Menominee',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah',
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Juneau',
  'America/Sitka',
  'America/Metlakatla',
  'America/Yakutat',
  'America/Nome',
  'America/Adak',
  'Pacific/Honolulu',
];

const getTimezoneAbbrIntl = (zone: string) =>
  new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
    .formatToParts(new Date())
    .find(part => part.type === 'timeZoneName')?.value ?? zone;

export const getTimezoneOptions = () => US_TIMEZONES.map(zone => ({
  label: `${zone} (${getTimezoneAbbrIntl(zone)})`,
  value: zone,
}));

export const getTimezoneAbbr = (zone: string) => getTimezoneAbbrIntl(zone);

export const getUserOwner = (user: any) => {
  const owner: any = {
    name: null,
    id: null,
    route: null,
    type: null,
  };
  if (UserRole.isAdminType(user.userRoleId)) {
    owner.name = config.APP_TITLE;
    owner.type = 'mav';
  } else if (UserRole.isDealerGroupManager(user.userRoleId)) {
    owner.name = user.dealerGroupName;
    owner.id = user.dealerGroupId;
    owner.route = 'view_dealer_group';
    owner.type = 'dealerGroup';
  } else if (UserRole.isDealerRegionalManager(user.userRoleId)) {
    owner.name = user.dealerRegionName;
    owner.id = user.dealerRegionId;
    owner.route = 'view_dealer_region';
    owner.type = 'dealerRegion';
    owner.parentName = user.dealerGroupName;
    owner.parentId = user.dealerGroupId;
    owner.parentRoute = 'view_dealer_group';
  } else if (UserRole.isDealerType(user.userRoleId)) {
    owner.name = user.dealerStoreName;
    owner.id = user.dealerStoreId;
    owner.route = 'view_dealer_store';
    owner.type = 'dealerStore';
  }
  return owner;
}

export const withAuth = (fragment: string) => buildUrl(config.AUTH_PATH_PREFIX as string, fragment);

export const mergeRefs = (...refs: any[]) => {
  const filteredRefs = refs.filter(Boolean);
  if (!filteredRefs.length) return null;
  if (filteredRefs.length === 0) return filteredRefs[0];
  return (inst: any) => {
    for (const ref of filteredRefs) {
      if (typeof ref === 'function') {
        ref(inst);
      } else if (ref) {
        ref.current = inst;
      }
    }
  };
};

export const dictionaryToSelectOptions = (dict: any) => Object.entries(dict).map(entry => ({ label: entry[1], value: entry[0] }));

export const formatPhone = (phone: string) => {
  if (!phone) {
    return;
  }
  const clean = phone.replace(/\D/g, '');
  const match = clean.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (!match) {
    return clean;
  }
  let result = '';
  if (!!match[1]) {
    result += match[1];
  }
  if (!!match[2]) {
    result += '-' + match[2];
  }
  if (!!match[3]) {
    result += '-' + match[3];
  }
  return result;
}

export const sanitizeHtml = (html: string) => DOMPurify.sanitize(html);

export const stripTags = (value: string) => value.replace(/(<([^>]+)>)/gi, "");

export const getInitialsFromName = (name: string) => name?.match(/\b(\w)/g)?.join('');

export const filterObjectKeys = (obj: any, keys: string[]) => {
  if (!obj) {
    return null;
  }
  return Object.fromEntries(Object.entries(obj).filter(([key]) => keys.includes(key)));
}

export const phoneHasNoOfDigits = (value: string) => {
  return !!value && value.replace(regx.phoneChars, '').length == config.PHONE_NR_OF_DIGITS;
}

export const randomStringSync = (len: number) => {
  const array = new Uint8Array(len);
  window.crypto.getRandomValues(array);

  return Array.from(array)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

export const getDefaultWorkingHours = (): WorkingHoursEntry[] =>
  WEEKDAYS.map((day: string) => ({
    day,
    startTime: '',
    endTime: '',
    isDayOff: false,
  }));

export const parseWorkingInterval = (workingInterval: any): WorkingHoursEntry[] => {
  let parsed = workingInterval;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return getDefaultWorkingHours();
    }
  }
  if (Array.isArray(parsed) && parsed.length === 7) {
    return parsed;
  }
  return getDefaultWorkingHours();
};